import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import GeoJSON from 'ol/format/GeoJSON';
import { DrawModifyInteraction, getGeometryDrawingStyle } from 'cadenza/map/layer-actions/draw-modify-interaction';
import SourceVector from 'ol/source/Vector';
import LayerVector from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import { EditGeometryPanel } from 'cadenza/map/edit-geometry-panel/edit-geometry-panel';
import { getLayerFeatureAttributeValues } from 'cadenza/api-client/maps-api';
import { isPanelAlreadyOpen } from 'cadenza/map/feature-attributes-panel/abstract-feature-panel';
import { addSidebar } from 'cadenza/map/map-utils';
import i18n from 'cadenza/map/layer-actions/layer-actions.properties';

import { LayerAction } from './layer-action';

export class FeatureEditGeometry extends LayerAction {

  #map;
  #geoJSON;

  constructor (layer, options) {
    super(layer, Object.assign({
      label: i18n('featureEditor.editGeometry')
    }, options));
    this.#map = options.map;
    this.#geoJSON = new GeoJSON();
  }

  execute () {
    const geometryType = getGeometryType(this.layer);
    const drawLayer = createDrawLayer(geometryType, this.#map.get(MapProperty.DEFAULT_STYLES));
    drawLayer.setMap(this.#map);
    this.#map.set(MapProperty.PRIMARY_INTERACTION, getDrawModifyInteraction(this.#map, geometryType, drawLayer));

    const editGeometryPanel = new EditGeometryPanel({
      map: this.#map,
      layer: this.layer,
      drawLayerSource: drawLayer.getSource(),
      geometryType,
      onClose: () => this.checkIfActionIsSupported()
    });
    addSidebar(editGeometryPanel);
    this.checkIfActionIsSupported();

    const layerId = this._layer.get(LayerProperty.ID);
    const featureId = this._layer.get(LayerProperty.SELECTED_FEATURE_IDS)[0];

    return getLayerFeatureAttributeValues(layerId, featureId)
      .then(({ geometry: geoJSONGeometry }) => {
        const geometry = this.#geoJSON.readGeometry(geoJSONGeometry);
        drawLayer.getSource().addFeature(new Feature({ geometry }));
      });
  }

  isSupportedSync ({ isEditingLayer }) {
    return !isPanelAlreadyOpen() && !isEditingLayer && this._layer.get(LayerProperty.HAS_FEATURES)
      && this._layer.get(LayerProperty.SELECTED_FEATURE_IDS).length === 1 && this._layer.get(LayerProperty.SUPPORTS_GEOMETRY_EDIT);
  }

  requiresObjectSelection () {
    return true;
  }

}

export function createDrawLayer (geometryType, styles, layerOpts) {
  const source = new SourceVector();
  const style = getGeometryDrawingStyle(geometryType, styles);
  return new LayerVector({
    source,
    style,
    ...layerOpts
  });
}

export function getDrawModifyInteraction (map, geometryType, drawLayer) {
  const drawAndModifyInteraction = new DrawModifyInteraction(map, { type: geometryType, source: drawLayer.getSource() });
  drawAndModifyInteraction.on('change:active', event => {
    if (event.oldValue) { // When deactivating the interaction remove the layer from the map
      drawLayer.setMap(null);
    }
  });
  return drawAndModifyInteraction;
}

export function getGeometryType (layer) {
  const geometryType = layer.get(LayerProperty.GEOMETRY_TYPE);
  return parseGeometryType(geometryType);
}

export function parseGeometryType (geometryType) {
  const geometryTypes = geometryType.split(' '); // Sometimes our geometry type looks like: 'Polygon MultiPolygon'
  if (geometryTypes.length > 0) {
    const sortedGeometryTypes = geometryTypes.sort((a, b) => b.length - a.length);
    return sortedGeometryTypes[0];
  }
  return geometryType;
}
